<template>
  <div class="public-raise-index">
    <!-- Page Header -->
    <div class="page-header" v-if="publicToken && tokenRequest">
      <div class="page-header__icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
          <path d="M12 6V12L16 14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div>
        <h1 class="page-header__title">
          {{ publicToken.name ? `${publicToken?.name} (${publicToken?.symbol})` : "Your Token" }}
        </h1>
        <p class="page-header__subtitle">Manage your public raise campaign</p>
      </div>
    </div>

    <!-- Pending/Rejected Token Request Card -->
    <div
      v-if="
        (!organisationDetails.products.includes('PUBLIC_RAISE_PENDING') &&
          tokenRequest?.status === 'Pending') ||
        (!organisationDetails.products.includes('PUBLIC_RAISE_PENDING') &&
          tokenRequest?.status === 'Rejected')
      "
      class="token-request-card"
    >
      <div
        class="status-badge"
        :class="tokenRequest?.status === 'Pending' ? 'status-badge--warning' : 'status-badge--error'"
      >
        <svg v-if="tokenRequest?.status === 'Pending'" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="7" cy="7" r="6" stroke="currentColor" stroke-width="1.5"/>
          <path d="M7 4V7L9 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7 13C10.3137 13 13 10.3137 13 7C13 3.68629 10.3137 1 7 1C3.68629 1 1 3.68629 1 7C1 10.3137 3.68629 13 7 13Z" stroke="currentColor" stroke-width="1.5"/>
          <path d="M9 5L5 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M5 5L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        {{ tokenRequest?.status === "Pending" ? "Pending Approval" : "Request Rejected" }}
      </div>

      <div class="token-request-content">
        <div class="token-image">
          <img :src="tokenRequest.image" alt="Token" />
        </div>
        <div class="token-stats">
          <div class="stat-item">
            <span class="stat-item__label">Token Name</span>
            <span class="stat-item__value">{{ tokenRequest.name }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-item__label">Token Symbol</span>
            <span class="stat-item__value">{{ tokenRequest.symbol }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-item__label">Target Raise</span>
            <span class="stat-item__value">{{ formatAmountToDollar(tokenRequest.raise_amount, 2, tokenRequest.currency || "USD") }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-item__label">Valuation</span>
            <span class="stat-item__value">{{ formatAmountToDollar(tokenRequest.valuation, 2, tokenRequest.currency || "USD") }}</span>
          </div>
        </div>
        <button
          class="configure-btn"
          @click="$router.push({ name: 'CreateAssetInfo', params: { type: 'public-raise' }, query: { asset: `${assetId}` } })"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 3.33334V12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M3.33334 8H12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Configure
        </button>
      </div>
    </div>

    <!-- Approved Token View -->
    <div v-if="tokenRequest?.status === 'Approved'" class="approved-section">
      <TokenCard :token="publicToken || {}" />

      <div class="order-book-section">
        <h2 class="section-title">Order Book</h2>
        <div class="order-book-card">
          <SectionDropdown title="order book">
            <template v-slot:content>
              <div class="order-book-content">
                <PublicRaiseOrderBookTable />
              </div>
            </template>
          </SectionDropdown>
        </div>
      </div>
    </div>

    <!-- Status Cards -->
    <div v-if="organisationDetails.products.includes('PUBLIC_RAISE_PENDING')">
      <ApplicationStatus status="pending" />
    </div>
    <div v-if="organisationDetails.products.includes('PUBLIC_RAISE_REVIEW')">
      <ApplicationStatus status="review" />
    </div>
    <div v-if="organisationDetails.products.includes('PUBLIC_RAISE_REJECTED')">
      <ApplicationStatus status="rejected" />
    </div>
    <div
      v-if="
        !organisationDetails.products.includes('PUBLIC_RAISE_PENDING') &&
        !organisationDetails.products.includes('PUBLIC_RAISE_REVIEW') &&
        !organisationDetails.products.includes('PUBLIC_RAISE_REJECTED') &&
        !tokenRequest
      "
    >
      <ApplicationStatus status="approved" />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from "vuex";
import TokenCard from "@/components/publicRaise/TokenCard.vue";
import ApplicationStatus from "@/components/publicRaise/ApplicationStatus.vue";
import PublicRaiseOrderBookTable from "@/components/tables/publicRaise/PublicRaiseOrderBookTable.vue";
import EmptyState from "@/components/notification/EmptyState";
import Btn from "@/components/general/BtnComponent.vue";
import SectionDropdown from "@/components/general/SectionDropdown.vue";
import { formatAmountToDollar } from "@/utils/helpers.js";
import { getItem } from "@/utils/storage.js";
import responseHandler from "@/utils/responseHandler.js";

export default {
  name: "PublicRaiseIndex",

  components: {
    TokenCard,
    PublicRaiseOrderBookTable,
    ApplicationStatus,
    Btn,
    SectionDropdown,
    EmptyState,
  },

  data() {
    return {
      loading: false,
      showUpdateForm: false,
      publicToken: {},
      assetId: "",
      title: "",
    };
  },

  computed: {
    ...mapGetters("organisationModule", ["tokens", "organisationDetails"]),
    ...mapState({
      tokenRequest: (state) => state.assetModule.tokenRequest,
      companyToken: (state) => state.organisationModule.tokens,
    }),
  },

  created() {
    this.assetId = getItem("org_details").asset;
    this.fetchToken();
  },

  methods: {
    ...mapActions("organisationModule", ["getTokens"]),
    ...mapActions("assetModule", ["getTokenRequest"]),

    formatAmountToDollar,

    async fetchToken() {
      this.loading = true;
      let tokens = this.getTokens();
      let tokenRequest = this.getTokenRequest();
      try {
        await Promise.all([tokens, tokenRequest]);
        this.currentToken();
        this.loading = false;
      } catch (error) {
        responseHandler.handleError(error);
      }
    },

    currentToken() {
      this.tokens.map((token) => {
        if (!token.entity && token.type !== "Private") this.publicToken = token;
      });
    },

    showForm(val) {
      this.showUpdateForm = val;
    },
  },
};
</script>

<style lang="scss" scoped>
.public-raise-index {
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  animation: fadeIn 0.3s ease;
  max-width: 1280px;
  margin: 0 auto;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

.page-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 32px;

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #4D84FF 0%, #6B9AFF 100%);
    border-radius: 12px;
    color: white;
    flex-shrink: 0;
  }

  &__title {
    font-size: 22px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 4px 0;
  }

  &__subtitle {
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
  }
}

.token-request-card {
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 20px;

  &--warning {
    background: #FEF3C7;
    color: #D97706;
  }

  &--error {
    background: #FEE2E2;
    color: #EF4444;
  }
}

.token-request-content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;
}

.token-image {
  width: 64px;
  height: 64px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.token-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px 32px;
  flex: 1;

  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &__label {
    font-size: 12px;
    font-weight: 400;
    color: #64748B;
  }

  &__value {
    font-size: 16px;
    font-weight: 500;
    color: #0F172A;
  }
}

.configure-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 24px;
  background: #4D84FF;
  border: none;
  border-radius: 8px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: white;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-left: auto;

  &:hover {
    background: #3B6EE8;
  }
}

.approved-section {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.order-book-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.section-title {
  font-size: 16px;
  font-weight: 500;
  color: #0F172A;
  margin: 0;
}

.order-book-card {
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 20px 24px;
}

.order-book-content {
  padding-top: 16px;
}
</style>
